/**
 * The caller's own team memberships.
 *
 * Its own module, small and dependency-light on purpose. The catalog adapter
 * needs it to stamp team ids on an auto-attach, and that adapter sits in the
 * app's entry chunk — importing it from `useTeamRoster` would pull the
 * collaboration and streaming clients in behind it for every page load.
 */
import * as React from "react";
import { useSession } from "@authe/ui-core/session";
import { useTeamsApi } from "@/api/adapters";

/** A team the caller belongs to, as a share target names it. */
export interface MyTeam {
  id: string;
  name: string;
}

/**
 * The membership read, with the difference between "no teams" and "didn't load".
 *
 * That distinction is the whole reason this shape exists. Every consumer of the
 * plain list treats it as an ANSWER: the share field offers it as the set of
 * teams you can pick, and `recordVisibility` maps a `team` default with no ids
 * onto a PRIVATE row. So a transient failure used to mean a person kept
 * creating tracers, rules and views they believed were shared with their team
 * and which nobody else could see — and the read that caused it had already
 * thrown its error away (`.catch(() => setTeams([]))`).
 */
export interface MyTeamsRead {
  /** The teams, or the last known list — never emptied by a failure. */
  teams: MyTeam[];
  /** The read is in flight and has never succeeded. */
  isLoading: boolean;
  /** The most recent read failed. `teams` may still hold an earlier answer. */
  failed: boolean;
  /** The failure itself, for a typed error surface. */
  error: unknown;
  /** True when this build has no memberships endpoint wired at all. */
  unavailable: boolean;
  /** Re-run the read. */
  refetch: () => void;
}

export function useMyTeamsRead(): MyTeamsRead {
  const { session } = useSession();
  const teamsApi = useTeamsApi();
  const userId = session?.user.id ?? null;
  const listMemberships = teamsApi.listMemberships;

  const [state, setState] = React.useState<{
    teams: MyTeam[];
    isLoading: boolean;
    error: unknown;
  }>({ teams: [], isLoading: true, error: null });
  const [attempt, setAttempt] = React.useState(0);

  React.useEffect(() => {
    if (!userId || !listMemberships) {
      // Not a failure: there is nothing to read. `unavailable` below says which.
      setState({ teams: [], isLoading: false, error: null });
      return undefined;
    }
    let alive = true;
    setState((prev) => ({ ...prev, isLoading: prev.teams.length === 0, error: null }));
    void listMemberships(userId)
      .then((ms) => {
        if (!alive) return;
        setState({
          teams: ms
            .filter((m) => Boolean(m.team.id))
            .map((m) => ({ id: m.team.id, name: m.team.name || m.team.id })),
          isLoading: false,
          error: null,
        });
      })
      .catch((error: unknown) => {
        if (!alive) return;
        // The previous list is KEPT. A refresh that fails must not retract an
        // answer the app already has, and it must never be mistaken for "you
        // are on no teams" — which is what silently privatised shared writes.
        setState((prev) => ({
          teams: prev.teams,
          isLoading: false,
          error: error ?? new Error("Could not load your teams"),
        }));
      });
    return () => {
      alive = false;
    };
  }, [listMemberships, userId, attempt]);

  const refetch = React.useCallback(() => setAttempt((n) => n + 1), []);

  return {
    teams: state.teams,
    isLoading: state.isLoading && Boolean(userId) && Boolean(listMemberships),
    failed: state.error != null,
    error: state.error,
    unavailable: !listMemberships,
    refetch,
  };
}

/**
 * Distinct from `useTeamIds` in `useTeamRoster`, which is gated on a mounted
 * streaming client because channel subscription is all it is for. Sharing is
 * not: an attach scoped to a team needs those ids on a page with no live
 * transport, and gating them there means a "team" write that reaches nobody.
 *
 * Returns the list alone. A caller that WRITES a team-scoped row wants
 * {@link useMyTeamsRead} instead — the list on its own cannot tell an empty
 * answer from a missing one, and treating those the same is what made a failed
 * read privatise a share.
 */
export function useMyTeams(): MyTeam[] {
  return useMyTeamsRead().teams;
}
